import React, { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { submitCampaignHostEnterpriseSupport } from '../../../redux/campaignHostSubscription/actions';
import { BRAND_LUXE_PRIMARY, BRAND_OUTLINE_VARIANT, BRAND_SURFACE_CONTAINER_LOW } from '../brandDashboardTokens';

const DIALOG_Z = 1600;

export interface CampaignHostEnterpriseContactDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called after a successful submit (parent may close tier modal). */
    onSubmitted?: () => void;
}

export function CampaignHostEnterpriseContactDialog({
    open,
    onClose,
    onSubmitted,
}: CampaignHostEnterpriseContactDialogProps) {
    const dispatchPromise = useDispatchPromise();
    const [creatorsNeeded, setCreatorsNeeded] = useState('10000');
    const [additionalDetails, setAdditionalDetails] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setCreatorsNeeded('10000');
        setAdditionalDetails('');
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await dispatchPromise(
                submitCampaignHostEnterpriseSupport({
                    creators_needed: Number(creatorsNeeded),
                    additional_details: additionalDetails,
                }),
            );
            showToastMessage('Thanks — Thspian sales staff will be in touch shortly.', 'success');
            reset();
            onSubmitted?.();
            onClose();
        } catch (err: unknown) {
            const message =
                err &&
                typeof err === 'object' &&
                'message' in err &&
                typeof (err as { message: unknown }).message === 'string'
                    ? (err as { message: string }).message
                    : 'Could not send your inquiry. Please try again.';
            showToastMessage(message, 'error');
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            sx={{ zIndex: DIALOG_Z }}
            slotProps={{
                backdrop: { sx: { zIndex: DIALOG_Z - 1 } },
            }}
            PaperProps={{
                sx: {
                    zIndex: DIALOG_Z,
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: 6,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                    overflow: 'hidden',
                },
            }}>
            <form onSubmit={handleSubmit}>
                <DialogTitle
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 20,
                        color: '#fff',
                        pb: 1,
                    }}>
                    Contact Support — Enterprise
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.75)',
                            lineHeight: 1.6,
                            mb: 2.5,
                        }}>
                        Tell us about your program. Thspian sales staff will follow up to set up Campaign Host
                        Enterprise.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            required
                            type="number"
                            label="Number of creators needed"
                            value={creatorsNeeded}
                            onChange={e => setCreatorsNeeded(e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                            disabled={submitting}
                            sx={fieldSx}
                        />
                        <TextField
                            required
                            multiline
                            minRows={4}
                            label="Additional details"
                            placeholder="Campaign volume, markets, timeline, or anything else we should know…"
                            value={additionalDetails}
                            onChange={e => setAdditionalDetails(e.target.value)}
                            fullWidth
                            disabled={submitting}
                            sx={fieldSx}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
                    <Button
                        type="button"
                        onClick={handleClose}
                        disabled={submitting}
                        sx={{ color: '#adaaaa', borderRadius: 99, px: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={submitting}
                        variant="contained"
                        sx={{
                            bgcolor: BRAND_LUXE_PRIMARY,
                            color: '#392700',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'none',
                            borderRadius: 99,
                            px: 3,
                            py: 1.25,
                            '&:hover': { bgcolor: '#feb700' },
                        }}>
                        {submitting ? <CircularProgress size={22} sx={{ color: '#392700' }} /> : 'Submit inquiry'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

const fieldSx = {
    '& .MuiInputLabel-root': { color: '#adaaaa' },
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        borderRadius: 3,
        '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}88`, borderRadius: 3 },
        '&:hover fieldset': { borderColor: BRAND_LUXE_PRIMARY },
        '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY },
    },
};
